/**
 * Local best results, kept in `localStorage`. There is no backend in v1 (CLAUDE.md), so
 * these are per-browser. A seed is recorded with each result so a score can be reproduced.
 */
export interface BestResult {
  score: number;
  stars: number;
  seed: number;
  /** Tuning challenges also record the integrated absolute error; lower is better. */
  iae?: number;
  at: string;
}

const KEY = 'setpoint.bests';

function load(): Record<string, BestResult> {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, BestResult>;
  } catch {
    return {};
  }
}

export function bestFor(plantId: string, scenarioId: string): BestResult | null {
  return load()[`${plantId}:${scenarioId}`] ?? null;
}

export function allBests(): Record<string, BestResult> {
  return load();
}

/** Records a result if it beats the stored one. Returns true when it did. */
export function recordBest(
  plantId: string,
  scenarioId: string,
  result: Omit<BestResult, 'at'>,
  lowerIsBetter = false,
): boolean {
  const all = load();
  const key = `${plantId}:${scenarioId}`;
  const prev = all[key];
  const better =
    !prev ||
    (lowerIsBetter
      ? (result.iae ?? Infinity) < (prev.iae ?? Infinity)
      : result.score > prev.score);
  if (!better) return false;
  all[key] = { ...result, at: new Date().toISOString() };
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    return false;
  }
  return true;
}
